import React, { useState, useEffect, useCallback } from "react";
import "../components/CSS/ReportsPage.css";
import logo from "../Assets/wowRevogo.png";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import {
  Star,
  TrendingUp,
  Download,
  MapPin,
  MessageSquare,
  Building,
  QrCode,
  Award,
  Target,
  Zap,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  Calendar,
  CalendarDays,
  Crown,
  TrendingDown,
  Minus,
  AlertCircle,
  CheckCircle,
  Lightbulb,
  BarChart3,
  Sparkles,
  Shield,
  Users,
  Loader2,
  History,
  X,
  RefreshCw,
  Clock,
  ClipboardList,
  Gauge,
  Utensils,
  UserCheck,
  Ban,
  FileText,
} from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  server_post_data,
  get_ai_business_report,
  get_ai_report_history,
  get_ai_report_detail,
} from "../ServiceConnection/serviceconnection";

// ============================================================================
// PDF DOWNLOAD
// ============================================================================

const downloadReportPDF = async (filename) => {
  const container = document.querySelector(".report-container");
  if (!container) return;

  await new Promise((r) => setTimeout(r, 80));
  container.classList.add("pdf-capturing");

  const originalAddColorStop = CanvasGradient.prototype.addColorStop;
  CanvasGradient.prototype.addColorStop = function (offset, color) {
    let safeOffset = offset;
    if (!Number.isFinite(safeOffset)) {
      safeOffset = 0;
    } else {
      safeOffset = Math.min(1, Math.max(0, safeOffset));
    }
    try {
      return originalAddColorStop.call(this, safeOffset, color);
    } catch (e) {
      console.warn("Skipped invalid gradient stop:", offset, color, e);
      return undefined;
    }
  };

  try {
    const pdf = new jsPDF("p", "mm", "a4");
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 8;
    const contentW = pageW - margin * 2;
    const pageContentH = pageH - margin * 2;
    const pageBottom = pageH - margin;
    const GAP = 3;

    const blocks = [];
    container.querySelectorAll(".report-section").forEach((section, sIdx) => {
      if (section.classList.contains("annual-cover-section")) {
        blocks.push({ el: section, forceNewPage: sIdx > 0 });
        return;
      }
      const mustStartNewPage =
        sIdx > 0 && !!section.querySelector(".annual-page-header");

      const kids = Array.from(section.children).filter(
        (el) => el.offsetHeight > 8,
      );
      if (!kids.length) {
        blocks.push({ el: section, forceNewPage: mustStartNewPage });
      } else {
        kids.forEach((el, i) =>
          blocks.push({ el, forceNewPage: mustStartNewPage && i === 0 }),
        );
      }
    });

    let cursorY = margin;
    let pageStarted = false;

    const newPage = () => {
      if (pageStarted) pdf.addPage();
      pageStarted = true;
      cursorY = margin;
    };
    newPage();

    for (const { el, forceNewPage } of blocks) {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        windowWidth: 1100,
      });

      if (!canvas.width || !canvas.height) continue;

      const imgH = (canvas.height * contentW) / canvas.width;

      if (forceNewPage && cursorY > margin + 0.5) newPage();

      if (imgH <= pageContentH) {
        if (cursorY + imgH > pageBottom) newPage();
        pdf.addImage(
          canvas.toDataURL("image/jpeg", 0.92),
          "JPEG",
          margin,
          cursorY,
          contentW,
          imgH,
        );
        cursorY += imgH + GAP;
      } else {
        if (cursorY > margin + 0.5) newPage();

        let rendered = 0;
        let lastSliceH = 0;
        while (imgH - rendered > 8) {
          if (rendered > 0) newPage();

          const sliceH = Math.min(pageContentH, imgH - rendered);
          const srcY = (rendered / imgH) * canvas.height;
          const srcH = (sliceH / imgH) * canvas.height;

          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = Math.max(1, Math.floor(srcH));
          const ctx = sliceCanvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          ctx.drawImage(
            canvas,
            0,
            srcY,
            canvas.width,
            srcH,
            0,
            0,
            canvas.width,
            srcH,
          );

          pdf.addImage(
            sliceCanvas.toDataURL("image/jpeg", 0.92),
            "JPEG",
            margin,
            margin,
            contentW,
            sliceH,
          );

          lastSliceH = sliceH;
          rendered += sliceH;
        }
        cursorY = margin + lastSliceH + GAP;
      }
    }

    pdf.save(filename);
  } finally {
    container.classList.remove("pdf-capturing");
    CanvasGradient.prototype.addColorStop = originalAddColorStop;
  }
};

// ============================================================================
// SHARED COMPONENTS
// ============================================================================

const PlatformIcon = ({ name }) => {
  const icons = {
    Google: { bg: "platform-google", text: "text-white", letter: "G" },
    Zomato: { bg: "platform-zomato", text: "text-white", letter: "Z" },
    TripAdvisor: {
      bg: "platform-tripadvisor",
      text: "text-white",
      letter: "T",
    },
    Facebook: { bg: "platform-facebook", text: "text-white", letter: "f" },
    "Booking.com": { bg: "platform-booking", text: "text-white", letter: "B" },
    "WowReviews QR": {
      bg: "platform-wowreviews",
      text: "text-white",
      letter: "W",
    },
  };
  const icon = icons[name] || {
    bg: "bg-secondary",
    text: "text-dark",
    letter: (name || "?").charAt(0).toUpperCase(),
  };
  return (
    <span className={`platform-icon ${icon.bg} ${icon.text}`}>
      {icon.letter}
    </span>
  );
};

const StarRating = ({ rating, size = "sm" }) => {
  if (typeof rating === "string")
    return <span className="star-rating-text">{rating}</span>;
  const fullStars = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  const starSize =
    size === "xl" ? "star-xl" : size === "lg" ? "star-lg" : "star-sm";
  return (
    <div className="d-flex align-items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`${starSize} ${
            i < fullStars
              ? "star-filled"
              : i === fullStars && hasHalf
                ? "star-half"
                : "star-empty"
          }`}
        />
      ))}
      <span
        className={`ms-1 fw-semibold ${
          size === "xl" ? "fs-4" : size === "lg" ? "fs-5" : "star-rating-number"
        }`}
      >
        {rating}
      </span>
    </div>
  );
};

const SentimentDonut = ({ positive, neutral, negative, size = "md" }) => {
  const total = positive + neutral + negative;
  if (!total) return null;
  const posPercent = (positive / total) * 100;
  const neuPercent = (neutral / total) * 100;
  const dims =
    size === "lg"
      ? { w: 160, r: 60, sw: 18 }
      : size === "sm"
        ? { w: 100, r: 35, sw: 12 }
        : { w: 120, r: 45, sw: 14 };
  const circumference = 2 * Math.PI * dims.r;
  const posLength = (posPercent / 100) * circumference;
  const neuLength = (neuPercent / 100) * circumference;
  const negLength = circumference - posLength - neuLength;

  return (
    <div className="d-flex align-items-center gap-4 flex-wrap">
      <div className="position-relative">
        <svg width={dims.w} height={dims.w} viewBox={`0 0 ${dims.w} ${dims.w}`}>
          <circle
            cx={dims.w / 2}
            cy={dims.w / 2}
            r={dims.r}
            fill="none"
            stroke="hsl(0, 84%, 60%)"
            strokeWidth={dims.sw}
            strokeDasharray={`${negLength} ${circumference}`}
            strokeDashoffset="0"
            transform={`rotate(-90 ${dims.w / 2} ${dims.w / 2})`}
            className="donut-shadow"
          />
          <circle
            cx={dims.w / 2}
            cy={dims.w / 2}
            r={dims.r}
            fill="none"
            stroke="hsl(220, 10%, 50%, 0.5)"
            strokeWidth={dims.sw}
            strokeDasharray={`${neuLength} ${circumference}`}
            strokeDashoffset={`${-negLength}`}
            transform={`rotate(-90 ${dims.w / 2} ${dims.w / 2})`}
          />
          <circle
            cx={dims.w / 2}
            cy={dims.w / 2}
            r={dims.r}
            fill="none"
            stroke="hsl(152, 69%, 45%)"
            strokeWidth={dims.sw}
            strokeDasharray={`${posLength} ${circumference}`}
            strokeDashoffset={`${-(negLength + neuLength)}`}
            transform={`rotate(-90 ${dims.w / 2} ${dims.w / 2})`}
            className="donut-shadow"
          />
          <text
            x={dims.w / 2}
            y={dims.w / 2 - 5}
            textAnchor="middle"
            className="donut-text-large"
          >
            {Math.round(posPercent)}%
          </text>
          <text
            x={dims.w / 2}
            y={dims.w / 2 + 12}
            textAnchor="middle"
            className="donut-text-small"
          >
            Positive
          </text>
        </svg>
      </div>
      <div className="sentiment-legend">
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="sentiment-dot sentiment-positive" />
          <span>
            Positive: <strong className="text-success">{positive}%</strong>
          </span>
        </div>
        <div className="d-flex align-items-center gap-2 mb-2">
          <span className="sentiment-dot sentiment-neutral" />
          <span>
            Neutral: <strong>{neutral}%</strong>
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="sentiment-dot sentiment-negative" />
          <span>
            Negative: <strong className="text-danger">{negative}%</strong>
          </span>
        </div>
      </div>
    </div>
  );
};

const safePct = (value, fallback = 0) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(0, n));
};

const AspectBar = ({ positive, negative }) => (
  <div className="aspect-bar">
    <div
      className="aspect-bar-positive"
      style={{ width: `${safePct(positive)}%` }}
    />
    <div
      className="aspect-bar-negative"
      style={{ width: `${safePct(negative)}%` }}
    />
  </div>
);

const ChangeBadge = ({ value, suffix = "", size = "sm" }) => {
  const num = Number(value) || 0;
  const isPositive = num > 0;
  const isNegative = num < 0;
  const sizeClass = size === "lg" ? "change-badge-lg" : "change-badge-sm";
  const formattedValue = Number.isInteger(num)
    ? num
    : parseFloat(num.toFixed(1));
  return (
    <span
      className={`change-badge ${sizeClass} ${
        isPositive
          ? "change-positive"
          : isNegative
            ? "change-negative"
            : "change-neutral"
      }`}
    >
      {isPositive ? (
        <TrendingUp className="change-icon" />
      ) : isNegative ? (
        <TrendingDown className="change-icon" />
      ) : null}
      {isPositive ? "+" : ""}
      {formattedValue}
      {suffix}
    </span>
  );
};

const TrendIcon = ({ trend }) => {
  if (trend === "up") return <TrendingUp className="trend-up" />;
  if (trend === "down") return <TrendingDown className="trend-down" />;
  return <Minus className="trend-stable" />;
};

const InsightCard = ({ children }) => (
  <div className="insight-card mt-3">
    <p className="insight-text">
      <Lightbulb size={16} className="insight-icon" />
      <span>
        <strong className="text-primary">AI Insight:</strong> {children}
      </span>
    </p>
  </div>
);

const ReportFooter = () => (
  <footer className="report-footer">
    <img src={logo} alt="WowReviews" className="footer-logo" />
    <p className="footer-text">
      Generated by WowReviews AI Audit Engine
      <br />
      <span className="text-primary">
        Helping businesses improve ratings, feedback &amp; guest experience
      </span>
    </p>
    <p className="footer-copyright">
      © {new Date().getFullYear()} WowReviews • www.wowreviews.co
    </p>
  </footer>
);

// ============================================================================
// API → REPORT SHAPE MAPPER
// ============================================================================

const mapApiToReport = (api) => {
  if (!api || !api.dashboard || !api.dashboard.total_reviews) return null;

  const dash = api.dashboard;
  const cmp = api.comparison || {};
  const aiRoot = api.ai || {};
  const ai = aiRoot.ai || {};
  const health = api.health_score || {};
  const dow = api.trend?.day_of_week || {};

  return {
    outlet: api.business?.business_name || "Your Business",
    generatedAt: new Date().toLocaleString(),
    period: { start: api.period?.start || "", end: api.period?.end || "" },

    executive: {
      prevAvg: cmp.rating?.previous ?? 0,
      currentAvg: cmp.rating?.current ?? dash.average_rating,
      ratingChange: cmp.rating?.change ?? 0,
      reviewVolumeChange: cmp.reviews?.change_percent ?? 0,
      positiveKeywords: ai.executive?.positive_keywords || [],
      mainNegativeIssue: ai.executive?.main_negative_issue || "-",
      opportunityInsight: ai.executive?.opportunity_insight || "-",
      verdict: ai.executive?.one_line_verdict || "",
      healthScore: health.score ?? 0,
      scoreChange: health.change ?? 0,
      grade: health.grade || "",
    },

    metrics: {
      totalReviews: dash.total_reviews ?? 0,
      avgRating: dash.average_rating ?? 0,
      negativeCount: dash.sentiment?.negative ?? 0,
      qrFeedback: dash.qr_feedback ?? 0,
      positive: dash.sentiment?.positive_percent ?? 0,
      neutral: dash.sentiment?.neutral_percent ?? 0,
      negative: dash.sentiment?.negative_percent ?? 0,
      responseRate: dash.response_rate ?? 0,
    },

    monthOverMonth: {
      rating: cmp.rating || {},
      reviews: cmp.reviews || {},
      positive: cmp.positive || {},
      negative: cmp.negative || {},
      responseRate: cmp.response_rate || {},
    },

    platforms: api.platforms || [],

    dayOfWeek: {
      peakDay: dow.peak_day || "",
      lowDay: dow.low_day || "",
      consistency: dow.consistency ?? 0,
      data: dow.data || [],
    },

    weeklyTrend: (api.trend?.weekly || []).map((w) => ({
      week: w.week,
      reviews: w.reviews,
      rating: w.average_rating,
    })),

    monthlyTrend: api.trend?.monthly || [],

    aspects: (aiRoot.aspect_summary || []).slice(0, 10).map((a) => ({
      name: a.aspect,
      positive: a.positive_percent ?? 0,
      negative:
        a.negative_percent ??
        Math.round((100 - (a.positive_percent ?? 0)) * 10) / 10,
      mentions: a.mentions ?? 0,
      evidence: a.evidence || [],
    })),

    positiveHighlights: ai.positive_highlights || [],
    featuredPositiveReview: api.featured_reviews?.positive || "",
    negativeIssues: (ai.issue_clusters || []).map((i) => ({
      text: i.issue,
      severity: i.severity,
      count: i.count,
      rootCause: i.root_cause || "",
      impact: i.business_impact || "",
    })),
    featuredNegativeReview: api.featured_reviews?.negative || "",

    recommendations: ai.recommendations || {},
    recommendationsV2: api.recommendations?.items || [],
    recMatrix: api.recommendations?.matrix || [], // 👈 add
    recTotals: api.recommendations?.totals || {}, // 👈 add
    quickWins: ai.quick_wins || [],
    riskAlerts: ai.risk_alerts || [],
    staffInsight: ai.staff_insight || "",
    menuInsight: ai.menu_insight || "",
    summary: ai.summary || {},

    // NAYA — advanced layer
    narrative: api.narrative || {},
    actionPlan: api.action_plan || {},
    replyTemplates: api.reply_templates || [],
    entities: aiRoot.entities || { staff: [], items: [] },
    intents: aiRoot.intents || {},
    guestTypes: aiRoot.guest_types || [],

    topPerformer: api.top_performer
      ? {
          aspect: api.top_performer.aspect,
          score: api.top_performer.positive_percent ?? 0,
          mentions: api.top_performer.mentions ?? 0,
          positive: api.top_performer.positive ?? 0,
          negative: api.top_performer.negative ?? 0,
        }
      : null,

    needsAttention: api.needs_attention
      ? {
          aspect: api.needs_attention.aspect,
          score: api.needs_attention.positive_percent ?? 0,
          // yahi wo number hai jo card par dikhna chahiye
          negativeScore:
            api.needs_attention.negative_percent ??
            Math.round(
              (100 - (api.needs_attention.positive_percent ?? 0)) * 10,
            ) / 10,
          mentions: api.needs_attention.mentions ?? 0,
          positive: api.needs_attention.positive ?? 0,
          negative: api.needs_attention.negative ?? 0,
        }
      : null,

    outlets: (() => {
      const seen = new Set();
      return (api.outlets || []).filter((o) => {
        const key = o.bus_id ?? o.name;
        if (key == null || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    })(),
    emergingTrends: api.emerging_trends || null,
  };
};

// ============================================================================
// SUB SECTIONS
// ============================================================================

const HeaderSection = ({ d, title, periodLabel }) => (
  <>
    <header className="report-header">
      <div>
        <img src={logo} alt="WowReviews" className="logo" />
        <p className="logo-tagline">AI-Powered Reputation Management</p>
      </div>
      <div className="text-end">
        <h6 className="report-title">{title}</h6>
        <p className="report-period">{periodLabel}</p>
      </div>
    </header>

    <div className="outlet-card my-4">
      <div>
        <p className="outlet-label">Report For</p>
        <p className="outlet-name">
          <span className="outlet-icon">
            <Building size={16} />
          </span>
          {d.outlet}
        </p>
        <p className="outlet-generated">Generated: {d.generatedAt}</p>
      </div>
      <div className="text-center d-print-none">
        <div className="qr-container">
          <QrCode size={40} className="qr-icon" />
        </div>
        <p className="qr-label">Scan for Dashboard</p>
      </div>
    </div>
  </>
);

const MIN_RELIABLE_MENTIONS = 3;

const TopMetricCards = ({ d }) => {
  const top = d.topPerformer;
  const bad = d.needsAttention;

  return (
    <div className="row g-3 my-4">
      {/* -------- TOP PERFORMER -------- */}
      <div className="col-md-4 col-12">
        <div className="metric-card metric-success">
          <div className="metric-card-top">
            <p className="metric-label">Top Performer</p>
            <span className="metric-icon metric-icon-success">
              <Award size={18} />
            </span>
          </div>

          {top ? (
            <>
              <p className="metric-aspect text-capitalize">{top.aspect}</p>
              <p className="metric-value text-success">
                {top.score}%
                <span className="metric-value-unit">guests happy</span>
              </p>
              <p className="metric-sub">
                {top.positive} of {top.mentions}{" "}
                {top.mentions === 1 ? "mention" : "mentions"} were positive
              </p>
              {top.mentions < MIN_RELIABLE_MENTIONS && (
                <p className="metric-caveat">
                  Low sample — treat as early signal
                </p>
              )}
            </>
          ) : (
            <>
              <p className="metric-aspect text-muted">Not enough data</p>
              <p className="metric-sub">
                No aspect had enough mentions this period
              </p>
            </>
          )}
        </div>
      </div>

      {/* -------- HEALTH SCORE -------- */}
      <div className="col-md-4 col-12">
        <div className="metric-card metric-primary">
          <div className="metric-card-top">
            <p className="metric-label">Health Score</p>
            <span className="metric-icon metric-icon-primary">
              <Target size={18} />
            </span>
          </div>
          <p className="metric-value-large text-primary">
            {d.executive.healthScore}
            <span className="metric-value-unit">/ 100</span>
          </p>
          <div className="metric-badge-row">
            <ChangeBadge value={d.executive.scoreChange} suffix=" pts" />
            {d.executive.grade && (
              <span className="grade-pill">{d.executive.grade}</span>
            )}
          </div>
          <p className="metric-sub">vs previous period</p>
        </div>
      </div>

      {/* -------- NEEDS ATTENTION -------- */}
      <div className="col-md-4 col-12">
        <div
          className={`metric-card ${bad ? "metric-danger" : "metric-neutral"}`}
        >
          <div className="metric-card-top">
            <p className="metric-label">Needs Attention</p>
            <span
              className={`metric-icon ${bad ? "metric-icon-danger" : "metric-icon-muted"}`}
            >
              {bad ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
            </span>
          </div>

          {bad ? (
            <>
              <p className="metric-aspect text-danger text-capitalize">
                {bad.aspect}
              </p>
              {/* NEGATIVE % — card ke tone se match karta hai */}
              <p className="metric-value text-danger">
                {bad.negativeScore}%
                <span className="metric-value-unit">complained</span>
              </p>
              <p className="metric-sub">
                {bad.negative} of {bad.mentions}{" "}
                {bad.mentions === 1 ? "mention" : "mentions"} were negative
              </p>
              {bad.mentions < MIN_RELIABLE_MENTIONS && (
                <p className="metric-caveat">
                  Low sample — watch, don't overreact
                </p>
              )}
            </>
          ) : (
            <>
              <p className="metric-aspect text-success">All clear</p>
              <p className="metric-sub">
                No aspect fell below the concern threshold
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// NAYA: Narrative (consultant letter)
// ---------------------------------------------------------------------------

const NarrativeSection = ({ d, number }) => {
  const n = d.narrative;
  if (!n || !n.opening) return null;

  const paras = [
    { label: "Where you stand", text: n.opening },
    { label: "What worked", text: n.what_worked },
    { label: "What hurt you", text: n.what_hurt },
    { label: "Why it happened", text: n.root_cause_story },
    { label: "The commercial angle", text: n.money_angle },
    { label: "If nothing changes", text: n.if_you_do_nothing },
  ].filter((p) => p.text);

  return (
    <div className="mb-4">
      <h6 className="section-title">
        <span className="section-number">{number}</span>
        Consultant Read
        <span className="ai-badge">Plain English</span>
      </h6>

      {n.headline && (
        <div className="narrative-headline">
          <FileText size={18} />
          <span>{n.headline}</span>
        </div>
      )}

      <div className="narrative-body">
        {paras.map((p, i) => (
          <div key={i} className="narrative-para">
            <p className="narrative-label">{p.label}</p>
            <p className="narrative-text">{p.text}</p>
          </div>
        ))}
        {n.closing && <p className="narrative-closing">{n.closing}</p>}
      </div>

      {(n.talking_points || []).length > 0 && (
        <div className="talking-points-card">
          <p className="talking-points-title">
            <Users size={15} /> Staff meeting talking points
          </p>
          <ul className="highlights-list mb-0">
            {n.talking_points.map((t, i) => (
              <li key={i} className="highlight-item">
                <CheckCircle
                  size={15}
                  className="text-primary highlight-icon"
                />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// NAYA: Quick wins + Risk alerts
// ---------------------------------------------------------------------------

const QuickWinsSection = ({ d, number }) => {
  const wins = d.quickWins || [];
  const risks = d.riskAlerts || [];
  if (!wins.length && !risks.length) return null;

  return (
    <div className="mb-4">
      <h6 className="section-title">
        <span className="section-number">{number}</span>
        Quick Wins &amp; Risk Alerts
        <span className="ai-badge">This Week</span>
      </h6>

      <div className="row g-3">
        <div className="col-md-6 col-12">
          <p className="fw-semibold small mb-2">Do these now (low effort)</p>
          {wins.length === 0 && (
            <p className="text-muted small">No quick wins identified.</p>
          )}
          {wins.map((w, i) => (
            <div key={i} className="quickwin-card">
              <div className="quickwin-head">
                <Zap size={15} className="text-success" />
                <span className="quickwin-action">{w.action}</span>
              </div>
              <div className="quickwin-meta">
                <span className="chip chip-effort">Effort: {w.effort}</span>
                <span className="chip chip-impact">Impact: {w.impact}</span>
                {w.owner && <span className="chip chip-owner">{w.owner}</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="col-md-6 col-12">
          <p className="fw-semibold small mb-2">Watch out for</p>
          {risks.length === 0 && (
            <p className="text-muted small">No active risks flagged.</p>
          )}
          {risks.map((r, i) => (
            <div
              key={i}
              className={`risk-card risk-${(r.severity || "low").toLowerCase()}`}
            >
              <div className="risk-head">
                <AlertTriangle size={15} />
                <span className="risk-title">{r.risk}</span>
                <span className="risk-sev">{r.severity}</span>
              </div>
              {r.why_now && <p className="risk-why">{r.why_now}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// NAYA: Guest mix + intent gauges
// ---------------------------------------------------------------------------

const GuestBehaviourSection = ({ d, number }) => {
  const it = d.intents || {};
  const gt = d.guestTypes || [];
  if (!gt.length && !Object.keys(it).length) return null;

  const gauges = [
    {
      label: "Complaint Rate",
      value: it.complaint_percent,
      tone: "danger",
      suffix: "%",
    },
    {
      label: "Suggestions",
      value: it.suggestion_percent,
      tone: "primary",
      suffix: "%",
    },
    {
      label: "Would Return",
      value: it.revisit_yes_percent,
      tone: "success",
      suffix: "%",
    },
    {
      label: "High-Severity",
      value: it.high_severity_count,
      tone: "warning",
      suffix: "",
    },
  ].filter((g) => g.value !== undefined && g.value !== null);

  return (
    <div className="mb-4">
      <h6 className="section-title">
        <span className="section-number">{number}</span>
        Guest Behaviour Signals
        <span className="ai-badge">Per-Review AI</span>
      </h6>

      {gauges.length > 0 && (
        <div className="row g-3 mb-3">
          {gauges.map((g, i) => (
            <div className="col-md-3 col-6" key={i}>
              <div className={`gauge-card gauge-${g.tone}`}>
                <Gauge size={18} className="gauge-icon" />
                <p className="gauge-value">
                  {g.value}
                  {g.suffix}
                </p>
                <p className="gauge-label">{g.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {gt.length > 0 && (
        <div className="table-card">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>Guest Type</th>
                <th className="text-center">Reviews</th>
                <th className="text-center">Negatives</th>
                <th className="text-center">Negative %</th>
              </tr>
            </thead>
            <tbody>
              {gt.map((g) => (
                <tr key={g.type}>
                  <td className="fw-medium text-capitalize">
                    <span className="d-flex align-items-center gap-2">
                      <UserCheck size={15} className="text-primary" />
                      {g.type}
                    </span>
                  </td>
                  <td className="text-center fw-semibold">{g.count}</td>
                  <td className="text-center text-danger">{g.negative}</td>
                  <td className="text-center">{g.negative_percent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {gt.length > 0 &&
        (() => {
          const worst = [...gt].sort(
            (a, b) => b.negative_percent - a.negative_percent,
          )[0];
          if (worst.negative_percent <= 0) {
            return (
              <InsightCard>
                No guest segment shows elevated dissatisfaction this period —
                service quality is consistent across visit types.
              </InsightCard>
            );
          }
          return (
            <InsightCard>
              <span className="text-capitalize">{worst.type}</span> guests show
              the highest dissatisfaction at {worst.negative_percent}% (
              {worst.negative} of {worst.count}) — tailor service recovery for
              this segment first.
            </InsightCard>
          );
        })()}
    </div>
  );
};

// ---------------------------------------------------------------------------
// NAYA: Named staff / items
// ---------------------------------------------------------------------------

const EntitySection = ({ d, number }) => {
  const staff = d.entities?.staff || [];
  const items = d.entities?.items || [];
  if (!staff.length && !items.length) return null;

  const Table = ({ rows, icon: Icon, head }) => (
    <div className="table-card">
      <table className="table table-hover mb-0">
        <thead>
          <tr>
            <th>{head}</th>
            <th className="text-center">Mentions</th>
            <th className="text-center">Sentiment</th>
            <th className="text-center">Positive</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td className="fw-medium">
                <span className="d-flex align-items-center gap-2">
                  <Icon size={15} className="text-primary" />
                  {r.name}
                </span>
              </td>
              <td className="text-center fw-semibold">{r.mentions}</td>
              <td>
                <div className="d-flex justify-content-center">
                  <AspectBar
                    positive={r.positive_percent}
                    negative={100 - r.positive_percent}
                  />
                </div>
              </td>
              <td className="text-center text-success fw-semibold">
                {r.positive_percent}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="mb-4">
      <h6 className="section-title">
        <span className="section-number">{number}</span>
        Named in Reviews
        <span className="ai-badge">People &amp; Items</span>
      </h6>

      <div className="row g-3">
        {staff.length > 0 && (
          <div className="col-md-6 col-12">
            <p className="fw-semibold small mb-2">Staff mentioned by name</p>
            <Table rows={staff} icon={Users} head="Staff" />
            {d.staffInsight && <InsightCard>{d.staffInsight}</InsightCard>}
          </div>
        )}
        {items.length > 0 && (
          <div className="col-md-6 col-12">
            <p className="fw-semibold small mb-2">
              Items / facilities mentioned
            </p>
            <Table rows={items} icon={Utensils} head="Item" />
            {d.menuInsight && <InsightCard>{d.menuInsight}</InsightCard>}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

const ExecutiveSummarySection = ({ d, number, scoreLabel, compareLabel }) => (
  <div className="py-4">
    <h6 className="section-title">
      <span className="section-number">{number}</span>
      Executive Summary
      <span className="ai-badge">AI-Generated</span>
    </h6>

    {d.executive.verdict && (
      <div className="verdict-banner">
        <Sparkles size={16} />
        <span>{d.executive.verdict}</span>
      </div>
    )}

    <div className="executive-summary">
      <p className="summary-item">
        <span className="summary-emoji">⭐</span>
        <span>
          Rating moved from <strong>{d.executive.prevAvg}</strong> to{" "}
          <strong className="text-success">{d.executive.currentAvg}</strong>{" "}
          <ChangeBadge value={d.executive.ratingChange} />
        </span>
      </p>
      <p className="summary-item">
        <span className="summary-emoji">📈</span>
        <span>
          Review volume changed by{" "}
          <strong
            className={
              d.executive.reviewVolumeChange >= 0
                ? "text-success"
                : "text-danger"
            }
          >
            {d.executive.reviewVolumeChange >= 0 ? "+" : ""}
            {d.executive.reviewVolumeChange}%
          </strong>{" "}
          compared to {compareLabel}
        </span>
      </p>
      {d.executive.positiveKeywords.length > 0 && (
        <p className="summary-item">
          <span className="summary-emoji">😊</span>
          <span>
            Guests praised:{" "}
            <strong>{d.executive.positiveKeywords.join(", ")}</strong>
          </span>
        </p>
      )}
      <p className="summary-item">
        <span className="summary-emoji">⚠️</span>
        <span>
          Recurring issue:{" "}
          <strong className="text-danger">
            {d.executive.mainNegativeIssue}
          </strong>
        </span>
      </p>
      <p className="summary-item">
        <span className="summary-emoji">🚀</span>
        <span>
          Opportunity:{" "}
          <strong className="text-primary">
            {d.executive.opportunityInsight}
          </strong>
        </span>
      </p>
    </div>

    <div className="health-score-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <span className="health-score-label">
          {scoreLabel}
          {d.executive.grade && (
            <span className="grade-pill">{d.executive.grade}</span>
          )}
        </span>
        <div className="d-flex align-items-center gap-2">
          <span className="health-score-number">{d.executive.healthScore}</span>
          <span className="health-score-max">/100</span>
          <ChangeBadge value={d.executive.scoreChange} suffix=" pts" />
        </div>
      </div>
      <div className="health-score-bar">
        <div
          className="health-score-fill"
          style={{ width: `${safePct(d.executive.healthScore)}%` }}
        />
      </div>
    </div>
  </div>
);

const KeyMetricsSection = ({ d, number, title }) => (
  <>
    <div className="mb-3">
      <h6 className="section-title">
        <span className="section-number">{number}</span>
        {title}
      </h6>

      <div className="row g-3">
        <div className="col">
          <div className="key-metric key-metric-primary">
            <MessageSquare size={20} className="key-metric-icon text-primary" />
            <p className="key-metric-value text-primary">
              {d.metrics.totalReviews}
            </p>
            <p className="key-metric-label">Total Reviews</p>
          </div>
        </div>
        <div className="col">
          <div className="key-metric key-metric-success">
            <Star size={20} className="key-metric-icon text-warning" />
            <div className="d-flex justify-content-center">
              <StarRating rating={d.metrics.avgRating} size="lg" />
            </div>
            <p className="key-metric-label mt-1">Avg Rating</p>
          </div>
        </div>
        <div className="col">
          <div className="key-metric key-metric-danger">
            <ThumbsDown size={20} className="key-metric-icon text-danger" />
            <p className="key-metric-value text-danger">
              {d.metrics.negativeCount}
            </p>
            <p className="key-metric-label">Negatives</p>
          </div>
        </div>
        <div className="col">
          <div className="key-metric key-metric-muted">
            <Zap size={20} className="key-metric-icon" />
            <p className="key-metric-value">{d.metrics.responseRate}%</p>
            <p className="key-metric-label">Reply Rate</p>
          </div>
        </div>
        <div className="col">
          <div className="key-metric key-metric-muted">
            <QrCode size={20} className="key-metric-icon" />
            <p className="key-metric-value">{d.metrics.qrFeedback}</p>
            <p className="key-metric-label">QR Feedback</p>
          </div>
        </div>
      </div>
    </div>

    <div className="sentiment-card mb-4">
      <p className="sentiment-title">Sentiment Breakdown</p>
      <SentimentDonut
        positive={d.metrics.positive}
        neutral={d.metrics.neutral}
        negative={d.metrics.negative}
      />
    </div>
    {d.aiCoverage?.analyzed > 0 && (
      <p className="coverage-note">
        <MessageSquare size={12} />
        Aspect analysis based on <strong>{d.aiCoverage.analyzed}</strong>{" "}
        reviews with written feedback ({d.aiCoverage.coverage_percent}% of{" "}
        {d.aiCoverage.total_reviews} total). The remaining{" "}
        {d.aiCoverage.total_reviews - d.aiCoverage.analyzed} were rating-only.
      </p>
    )}
  </>
);

const PlatformSection = ({ d, number, compareLabel }) => (
  <div className="mb-4">
    <h6 className="section-title">
      <span className="section-number">{number}</span>
      Platform Performance
    </h6>

    {d.platforms.length === 0 ? (
      <p className="text-muted small">No platform data for this period.</p>
    ) : (
      <div className="table-card">
        <table className="table table-hover mb-0">
          <thead>
            <tr>
              <th>Platform</th>
              <th className="text-center">Reviews</th>
              <th className="text-center">Rating</th>
              <th className="text-center">{compareLabel}</th>
              <th className="text-center">Trend</th>
            </tr>
          </thead>
          <tbody>
            {d.platforms.map((p) => (
              <tr key={p.name}>
                <td>
                  <span className="d-flex align-items-center gap-2">
                    <PlatformIcon name={p.name} />
                    {p.name}
                  </span>
                </td>
                <td className="text-center fw-semibold">{p.reviews}</td>
                <td>
                  {p.rating ? (
                    <div className="d-flex justify-content-center">
                      <StarRating rating={p.rating} />
                    </div>
                  ) : (
                    <span className="text-muted small">Text feedback only</span>
                  )}
                </td>
                <td className="text-center text-muted small">
                  {p.prev_rating}
                </td>
                <td className="text-center">
                  <div className="d-flex justify-content-center">
                    <TrendIcon trend={p.trend} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {d.platforms.length > 0 && (
      <InsightCard>
        {d.platforms[0].name} leads with {d.platforms[0].reviews} reviews.{" "}
        {d.platforms.filter((p) => p.trend === "down").length > 0 &&
          `${d.platforms
            .filter((p) => p.trend === "down")
            .map((p) => p.name)
            .join(", ")} declined — investigate guest concerns.`}
      </InsightCard>
    )}
  </div>
);

const AspectSection = ({ d, number }) => (
  <div className="mb-4">
    <h6 className="section-title">
      <span className="section-number">{number}</span>
      Aspect-Based Sentiment
    </h6>

    {d.aspects.length === 0 ? (
      <p className="text-muted small">
        Not enough review text for aspect analysis.
      </p>
    ) : (
      <div className="table-card">
        <table className="table table-hover mb-0">
          <thead>
            <tr>
              <th>Aspect</th>
              <th className="text-center">Sentiment</th>
              <th className="text-center">
                <ThumbsUp size={12} className="text-success" />
              </th>
              <th className="text-center">
                <ThumbsDown size={12} className="text-danger" />
              </th>
              <th className="text-center">Mentions</th>
              <th>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {d.aspects.map((a) => (
              <tr key={a.name}>
                <td className="fw-medium text-capitalize">{a.name}</td>
                <td>
                  <div className="d-flex justify-content-center">
                    <AspectBar positive={a.positive} negative={a.negative} />
                  </div>
                </td>
                <td className="text-center text-success fw-semibold">
                  {a.positive}%
                </td>
                <td className="text-center text-danger fw-semibold">
                  {a.negative}%
                </td>
                <td className="text-center">{a.mentions}</td>
                <td className="evidence-cell">
                  {(a.evidence || []).slice(0, 1).map((q, i) => (
                    <span key={i} className="evidence-quote">
                      "{q}"
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {d.aspects.length > 0 && (
      <InsightCard>
        {d.needsAttention &&
          d.topPerformer &&
          d.needsAttention.aspect !== d.topPerformer.aspect && (
            <>
              <span className="text-capitalize">{d.needsAttention.aspect}</span>{" "}
              drew negative comments in {d.needsAttention.negative} of{" "}
              {d.needsAttention.mentions} mentions — fix this first.{" "}
            </>
          )}
        {d.topPerformer && (
          <>
            <span className="text-capitalize">{d.topPerformer.aspect}</span> is
            your strongest area at {d.topPerformer.score}% positive — use it in
            training and marketing.
          </>
        )}
      </InsightCard>
    )}
  </div>
);

const HighlightsSection = ({ d, posNumber, negNumber }) => (
  <div className="row g-3 mb-4">
    <div className="col-md-6 col-12">
      <h6 className="section-title">
        <span className="section-number section-number-success">
          {posNumber}
        </span>
        Positive Highlights
      </h6>

      <ul className="highlights-list mb-3">
        {d.positiveHighlights.slice(0, 5).map((h, i) => (
          <li key={i} className="highlight-item">
            <CheckCircle size={16} className="text-success highlight-icon" />
            <span>{h}</span>
          </li>
        ))}
      </ul>

      {d.featuredPositiveReview && (
        <div className="featured-review featured-review-positive">
          <p className="featured-review-label">Featured Review</p>
          <p className="featured-review-text">"{d.featuredPositiveReview}"</p>
        </div>
      )}
    </div>

    <div className="col-md-6 col-12">
      <h6 className="section-title">
        <span className="section-number section-number-danger">
          {negNumber}
        </span>
        Issue Clusters
      </h6>

      <ul className="highlights-list mb-3">
        {d.negativeIssues.map((issue, i) => (
          <li key={i} className="issue-block">
            <div className="issue-head">
              <span
                className={`severity-badge ${
                  issue.severity === "High"
                    ? "severity-high"
                    : issue.severity === "Medium"
                      ? "severity-medium"
                      : "severity-low"
                }`}
              >
                {issue.severity}
              </span>
              <span className="flex-fill">{issue.text}</span>
              {issue.count > 0 && (
                <span className="issue-count small text-muted">
                  ({issue.count})
                </span>
              )}
            </div>
            {issue.rootCause && (
              <p className="issue-sub">
                <strong>Root cause:</strong> {issue.rootCause}
              </p>
            )}
            {issue.impact && (
              <p className="issue-sub">
                <strong>Impact:</strong> {issue.impact}
              </p>
            )}
          </li>
        ))}
      </ul>

      {d.featuredNegativeReview && (
        <div className="featured-review featured-review-negative">
          <p className="featured-review-label">Representative Review</p>
          <p className="featured-review-text">"{d.featuredNegativeReview}"</p>
        </div>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// RECOMMENDATIONS — enhanced (ranked, scored, evidence-backed)
// Purane cached reports me recommendationsV2 nahi hoga → legacy fallback
// ---------------------------------------------------------------------------

const PRIORITY_META = {
  Critical: { cls: "prio-critical", icon: AlertCircle, dot: "#e02424" },
  High: { cls: "prio-high", icon: AlertTriangle, dot: "#f97316" },
  Medium: { cls: "prio-medium", icon: Lightbulb, dot: "#eab308" },
  Low: { cls: "prio-low", icon: CheckCircle, dot: "#22c55e" },
};

const QUADRANT_LABEL = {
  quick_win: "Quick Win",
  major_project: "Major Project",
  fill_in: "Fill-in",
  reconsider: "Reconsider",
};

const ScoreRing = ({ score }) => {
  const r = 20;
  const c = 2 * Math.PI * r;
  const filled = (safePct(score) / 100) * c;
  const tone =
    score >= 75
      ? "hsl(0, 84%, 60%)"
      : score >= 55
        ? "hsl(45, 93%, 47%)"
        : "hsl(199, 89%, 48%)";
  return (
    <svg width="52" height="52" viewBox="0 0 52 52" className="score-ring">
      <circle
        cx="26"
        cy="26"
        r={r}
        fill="none"
        stroke="hsl(220,13%,91%)"
        strokeWidth="5"
      />
      <circle
        cx="26"
        cy="26"
        r={r}
        fill="none"
        stroke={tone}
        strokeWidth="5"
        strokeDasharray={`${filled} ${c}`}
        strokeLinecap="round"
        transform="rotate(-90 26 26)"
      />
      <text x="26" y="30" textAnchor="middle" className="score-ring-text">
        {score}
      </text>
    </svg>
  );
};

const ImpactEffortMatrix = ({ points }) => {
  if (!points || points.length < 2) return null;
  return (
    <div className="matrix-wrap">
      <div className="matrix-box">
        <span className="matrix-q q-tl">
          High impact · Low effort
          <br />
          <b>Do first</b>
        </span>
        <span className="matrix-q q-tr">
          High impact · High effort
          <br />
          <b>Plan it</b>
        </span>
        <span className="matrix-q q-bl">
          Low impact · Low effort
          <br />
          <b>If time allows</b>
        </span>
        <span className="matrix-q q-br">
          Low impact · High effort
          <br />
          <b>Skip</b>
        </span>
        <span className="matrix-axis-v" />
        <span className="matrix-axis-h" />
        {points.map((p) => (
          <span
            key={p.rank}
            className={`matrix-dot dot-${(p.priority || "medium").toLowerCase()}`}
            style={{ left: `${p.x}%`, bottom: `${p.y}%` }}
            title={p.title}
          >
            {p.rank}
          </span>
        ))}
      </div>
      <div className="matrix-labels">
        <span className="matrix-ylabel">Impact →</span>
        <span className="matrix-xlabel">Effort →</span>
      </div>
    </div>
  );
};

const LegacyRecommendations = ({ d }) => {
  const cfg = [
    {
      key: "high",
      cls: "recommendation-high",
      icon: AlertCircle,
      label: "High Priority",
      arrow: "text-danger",
    },
    {
      key: "medium",
      cls: "recommendation-medium",
      icon: Lightbulb,
      label: "Medium Priority",
      arrow: "text-warning",
    },
    {
      key: "low",
      cls: "recommendation-low",
      icon: CheckCircle,
      label: "Low Priority",
      arrow: "text-success",
    },
  ];
  return (
    <div className="recommendations-container">
      {cfg.map(({ key, cls, icon: Icon, label, arrow }) => {
        const rec = d.recommendations?.[key];
        if (!rec || !rec.issue) return null;
        return (
          <div key={key} className={`recommendation-card ${cls}`}>
            <div className="recommendation-header">
              <Icon size={20} />
              <span className="recommendation-priority">{label}</span>
            </div>
            <p className="recommendation-issue">{rec.issue}</p>
            <ul className="recommendation-actions">
              {(rec.actions || []).map((a, i) => (
                <li key={i}>
                  <span className={arrow}>→</span>
                  {a}
                </li>
              ))}
            </ul>
            <div className="rec-meta">
              {rec.owner && (
                <span className="chip chip-owner">{rec.owner}</span>
              )}
              {rec.timeline && (
                <span className="chip chip-effort">{rec.timeline}</span>
              )}
              {rec.success_metric && (
                <span className="chip chip-impact">
                  KPI: {rec.success_metric}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const RecommendationsSection = ({ d, number }) => {
  const items = d.recommendationsV2 || [];
  const hasLegacy = ["high", "medium", "low"].some(
    (k) => d.recommendations?.[k]?.issue,
  );

  if (!items.length && !hasLegacy) return null;

  // purane cached report → legacy render
  if (!items.length) {
    return (
      <div className="mb-4">
        <h6 className="section-title">
          <span className="section-number">{number}</span>
          AI Improvement Plan
          <span className="ai-badge">Priority Actions</span>
        </h6>
        <LegacyRecommendations d={d} />
      </div>
    );
  }

  const t = d.recTotals || {};

  return (
    <div className="mb-4">
      <h6 className="section-title">
        <span className="section-number">{number}</span>
        Prioritised Improvement Plan
        <span className="ai-badge">Ranked by Impact</span>
      </h6>

      {/* ---- Summary strip ---- */}
      <div className="rec-summary-strip">
        <div className="rec-stat">
          <ClipboardList size={16} className="text-primary" />
          <span className="rec-stat-value">{t.total ?? items.length}</span>
          <span className="rec-stat-label">Recommendations</span>
        </div>
        <div className="rec-stat">
          <Zap size={16} className="text-success" />
          <span className="rec-stat-value">{t.quick_wins ?? 0}</span>
          <span className="rec-stat-label">Quick Wins</span>
        </div>
        <div className="rec-stat">
          <Target size={16} className="text-warning" />
          <span className="rec-stat-value">{t.total_actions ?? 0}</span>
          <span className="rec-stat-label">Action Steps</span>
        </div>
        <div className="rec-stat">
          <AlertCircle size={16} className="text-danger" />
          <span className="rec-stat-value">
            {(t.by_priority?.Critical || 0) + (t.by_priority?.High || 0)}
          </span>
          <span className="rec-stat-label">Urgent</span>
        </div>
      </div>

      {/* ---- Impact / Effort matrix ---- */}
      <ImpactEffortMatrix points={d.recMatrix} />

      {/* ---- Ranked cards ---- */}
      <div className="rec-list">
        {items.map((r) => {
          const meta = PRIORITY_META[r.priority] || PRIORITY_META.Medium;
          const Icon = meta.icon;
          return (
            <div key={r.rank} className={`rec-card ${meta.cls}`}>
              {/* header */}
              <div className="rec-card-head">
                <span className="rec-rank">#{r.rank}</span>
                <div className="rec-head-main">
                  <p className="rec-title">
                    <Icon size={15} />
                    {r.title}
                  </p>
                  <div className="rec-tags">
                    <span className="chip chip-prio">{r.priority}</span>
                    <span className="chip chip-cat">{r.category}</span>
                    {r.quadrant && (
                      <span className="chip chip-quad">
                        {QUADRANT_LABEL[r.quadrant] || r.quadrant}
                      </span>
                    )}
                    <span className="chip chip-effort">Effort: {r.effort}</span>
                    <span className="chip chip-impact">Impact: {r.impact}</span>
                    <span className="chip chip-cost">Cost: {r.cost}</span>
                  </div>
                </div>
                <div className="rec-score">
                  <ScoreRing score={r.score} />
                  <span className="rec-score-label">Priority</span>
                </div>
              </div>

              {/* problem / why / cause */}
              <div className="rec-why-grid">
                {r.problem && (
                  <div className="rec-why-item">
                    <p className="rec-why-label">The problem</p>
                    <p className="rec-why-text">{r.problem}</p>
                  </div>
                )}
                {r.root_cause && (
                  <div className="rec-why-item">
                    <p className="rec-why-label">Root cause</p>
                    <p className="rec-why-text">{r.root_cause}</p>
                  </div>
                )}
                {r.why_now && (
                  <div className="rec-why-item">
                    <p className="rec-why-label">Why now</p>
                    <p className="rec-why-text">{r.why_now}</p>
                  </div>
                )}
              </div>

              {/* evidence */}
              {(r.evidence_mentions > 0 || r.evidence_quotes?.length > 0) && (
                <div className="rec-evidence">
                  <p className="rec-evidence-head">
                    <MessageSquare size={13} />
                    Evidence
                    <span className="chip chip-conf">
                      {r.confidence} confidence
                    </span>
                    {r.evidence_mentions > 0 && (
                      <span className="rec-evidence-count">
                        {r.evidence_mentions} mentions · {r.evidence_negatives}{" "}
                        negative
                        {r.affected_aspect ? ` · ${r.affected_aspect}` : ""}
                      </span>
                    )}
                  </p>
                  {(r.evidence_quotes || []).map((q, i) => (
                    <p key={i} className="rec-evidence-quote">
                      "{q}"
                    </p>
                  ))}
                </div>
              )}

              {/* action steps */}
              <div className="rec-steps">
                <p className="rec-steps-head">
                  <ClipboardList size={13} /> Action steps
                </p>
                {r.actions.map((a, i) => (
                  <div key={i} className="rec-step">
                    <span className="rec-step-num">{i + 1}</span>
                    <div className="rec-step-body">
                      <p className="rec-step-text">{a.step}</p>
                      {a.how && <p className="rec-step-how">{a.how}</p>}
                      <div className="rec-step-meta">
                        <span className="chip chip-owner">{a.owner}</span>
                        <span className="chip chip-when">
                          <Clock size={10} /> {a.when}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* KPI target */}
              {r.success_metric?.metric && (
                <div className="rec-kpi">
                  <div className="rec-kpi-metric">
                    <Gauge size={14} />
                    <span>{r.success_metric.metric}</span>
                  </div>
                  <div className="rec-kpi-move">
                    <span className="kpi-current">
                      {r.success_metric.current}
                    </span>
                    <span className="kpi-arrow">→</span>
                    <span className="kpi-target">
                      {r.success_metric.target}
                    </span>
                    {r.success_metric.by_when && (
                      <span className="kpi-when">
                        by {r.success_metric.by_when}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* footer */}
              <div className="rec-footer">
                {r.expected_outcome && (
                  <p className="rec-foot-line rec-foot-good">
                    <CheckCircle size={13} />
                    <span>
                      <strong>Expected:</strong> {r.expected_outcome}
                    </span>
                  </p>
                )}
                {r.risk_if_ignored && (
                  <p className="rec-foot-line rec-foot-bad">
                    <AlertTriangle size={13} />
                    <span>
                      <strong>If ignored:</strong> {r.risk_if_ignored}
                    </span>
                  </p>
                )}
                {r.common_mistake && (
                  <p className="rec-foot-line rec-foot-warn">
                    <Ban size={13} />
                    <span>
                      <strong>Common mistake:</strong> {r.common_mistake}
                    </span>
                  </p>
                )}
                <div className="rec-foot-chips">
                  {r.timeline && (
                    <span className="chip chip-effort">{r.timeline}</span>
                  )}
                  {r.cost_note && (
                    <span className="chip chip-cost">{r.cost_note}</span>
                  )}
                  {r.dependencies && (
                    <span className="chip chip-effort">
                      Needs: {r.dependencies}
                    </span>
                  )}
                  {r.who_signs_off && (
                    <span className="chip chip-owner">
                      Sign-off: {r.who_signs_off}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const OutletsSection = ({ d, number }) => {
  if (!d.outlets.length) return null;
  return (
    <div className="mb-4">
      <h6 className="section-title">
        <span className="section-number">{number}</span>
        Outlet Comparison
      </h6>

      <div className="table-card">
        <table className="table table-hover mb-0">
          <thead>
            <tr>
              <th>Outlet</th>
              <th className="text-center">Rating</th>
              <th className="text-center">Reviews</th>
              <th className="text-center">Negatives</th>
              <th className="text-center">Trend</th>
            </tr>
          </thead>
          <tbody>
            {d.outlets.map((outlet, idx) => (
              <tr key={outlet.bus_id ?? outlet.name ?? idx}>
                <td className="fw-medium">
                  <span className="d-flex align-items-center gap-2">
                    <MapPin size={16} className="text-primary" />
                    {outlet.name}
                  </span>
                </td>
                <td>
                  <div className="d-flex justify-content-center">
                    {outlet.rating ? (
                      <div className="d-flex justify-content-center">
                        <StarRating rating={outlet.rating} />
                      </div>
                    ) : (
                      <span className="text-muted small">No rating data</span>
                    )}
                  </div>
                </td>
                <td className="text-center fw-semibold">{outlet.reviews}</td>
                <td className="text-center text-danger fw-semibold">
                  {outlet.negatives}
                </td>
                <td className="text-center">
                  <div className="d-flex justify-content-center">
                    <TrendIcon trend={outlet.trend} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EmergingTrendsSection = ({ d, number }) => {
  const t = d.emergingTrends;
  if (!t || !t.has_data) return null;

  const hasAnything =
    t.increasing.length ||
    t.decreasing.length ||
    t.new_topics.length ||
    t.resolved_topics.length;
  if (!hasAnything) return null;

  const Row = ({ icon: Icon, iconClass, label, sub, tone }) => (
    <li className="highlight-item">
      <Icon size={16} className={`highlight-icon ${iconClass}`} />
      <span className="flex-fill text-capitalize">{label}</span>
      <span className={`small ${tone}`}>{sub}</span>
    </li>
  );

  return (
    <div className="mb-4">
      <h6 className="section-title">
        <span className="section-number">{number}</span>
        Emerging Trends
        <span className="ai-badge">vs Previous Period</span>
      </h6>

      <div className="row g-3">
        <div className="col-md-6 col-12">
          <p className="fw-semibold small mb-2">Trending Up</p>
          <ul className="highlights-list mb-3">
            {t.increasing.map((r) => (
              <Row
                key={r.aspect}
                icon={TrendingUp}
                iconClass={
                  r.concern_level === "warning" ? "text-danger" : "text-success"
                }
                label={r.aspect}
                sub={`+${r.change_percent}%`}
                tone={
                  r.concern_level === "warning" ? "text-danger" : "text-success"
                }
              />
            ))}
            {t.new_topics.map((r) => (
              <Row
                key={`new-${r.aspect}`}
                icon={Sparkles}
                iconClass="text-primary"
                label={`${r.aspect} (new)`}
                sub={`${r.mentions} mentions`}
                tone="text-primary"
              />
            ))}
            {!t.increasing.length && !t.new_topics.length && (
              <li className="text-muted small">No significant increases.</li>
            )}
          </ul>
        </div>

        <div className="col-md-6 col-12">
          <p className="fw-semibold small mb-2">Trending Down</p>
          <ul className="highlights-list mb-3">
            {t.decreasing.map((r) => (
              <Row
                key={r.aspect}
                icon={TrendingDown}
                iconClass={
                  r.concern_level === "warning" ? "text-danger" : "text-success"
                }
                label={r.aspect}
                sub={`${r.change_percent}%`}
                tone={
                  r.concern_level === "warning" ? "text-danger" : "text-success"
                }
              />
            ))}
            {t.resolved_topics.map((r) => (
              <Row
                key={`res-${r.aspect}`}
                icon={r.kind === "resolved" ? CheckCircle : TrendingDown}
                iconClass={
                  r.kind === "resolved" ? "text-success" : "text-warning"
                }
                label={`${r.aspect} ${r.kind === "resolved" ? "(resolved)" : "(no longer praised)"}`}
                sub={`was ${r.previous_mentions} mentions`}
                tone={r.kind === "resolved" ? "text-success" : "text-warning"}
              />
            ))}
            {!t.decreasing.length && !t.resolved_topics.length && (
              <li className="text-muted small">No significant decreases.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

const SummarySection = ({ d, number, title }) => (
  <div className="mb-4">
    <h6 className="section-title">
      <span className="section-number">{number}</span>
      {title}
    </h6>

    <div className="summary-card">
      <div className="summary-card-item">
        <span className="summary-emoji">⭐</span>
        <span>
          Overall reputation is{" "}
          <strong
            className={
              d.summary.status === "declining" ? "text-danger" : "text-success"
            }
          >
            {d.summary.status || "-"}
          </strong>
        </span>
      </div>
      <div className="summary-card-item">
        <span className="summary-emoji">🚀</span>
        <span>
          Guests loved <strong>{d.summary.positive_key || "-"}</strong>
        </span>
      </div>
      <div className="summary-card-item">
        <span className="summary-emoji">⚠️</span>
        <span>
          Critical issue:{" "}
          <strong className="text-danger">
            {d.summary.major_issue || "-"}
          </strong>
        </span>
      </div>
      <div className="summary-card-item">
        <span className="summary-emoji">📉</span>
        <span>
          Response: <strong>{d.metrics.responseRate}% response rate</strong>
        </span>
      </div>
      <div className="summary-card-item">
        <span className="summary-emoji">🧠</span>
        <span>
          <strong className="text-primary">Action:</strong>{" "}
          {d.summary.action || "-"}
        </span>
      </div>
    </div>
  </div>
);

// ============================================================================
// WEEKLY / MONTHLY REPORT
// ============================================================================

const PeriodReport = ({ data: d, type }) => {
  const isWeekly = type === "weekly";
  const title = isWeekly ? "Weekly AI Audit Report" : "Monthly AI Audit Report";
  const compareLabel = isWeekly ? "last week" : "last month";
  const scoreLabel = isWeekly
    ? "Weekly Reputation Health Score"
    : "Monthly Reputation Health Score";

  const maxDayCount = Math.max(...d.dayOfWeek.data.map((x) => x.count), 1);
  const maxWeekReviews = Math.max(...d.weeklyTrend.map((x) => x.reviews), 1);

  // section numbers dynamic — naye sections beech me aa gaye hain
  let n = 0;
  const next = () => ++n;

  const nExec = next();
  const nMetrics = next();
  const nNarrative = next();
  const nMoM = isWeekly ? null : next();
  const nPlatform = next();
  const nTrend = next();
  const nAspect = next();
  const nEntity = next();
  const nGuest = next();
  const nEmerging = next();
  const nPos = next();
  const nNeg = next();
  const nQuick = next();
  const nRec = next();
  const nOutlets = next();
  const nSummary = next();

  return (
    <div className="report-container">
      {/* PAGE 1 */}
      <section className="report-section">
        <HeaderSection
          d={d}
          title={title}
          periodLabel={`${d.period.start} – ${d.period.end}`}
        />
        <TopMetricCards d={d} />
        <ExecutiveSummarySection
          d={d}
          number={nExec}
          scoreLabel={scoreLabel}
          compareLabel={compareLabel}
        />
        <KeyMetricsSection
          d={d}
          number={nMetrics}
          title={isWeekly ? "Key Weekly Metrics" : "Key Monthly Metrics"}
        />
        <NarrativeSection d={d} number={nNarrative} />
      </section>

      <div className="page-break" />

      <section className="report-section">
        {!isWeekly && (
          <div className="mb-4">
            <h6 className="section-title">
              <span className="section-number">{nMoM}</span>
              Month-over-Month Comparison
            </h6>
            <div className="row g-3">
              <div className="col-md-3 col-6">
                <div className="mom-card">
                  <p className="mom-label">Avg Rating</p>
                  <p className="mom-value">{d.monthOverMonth.rating.current}</p>
                  <ChangeBadge value={d.monthOverMonth.rating.change} />
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="mom-card">
                  <p className="mom-label">Total Reviews</p>
                  <p className="mom-value">
                    {d.monthOverMonth.reviews.current}
                  </p>
                  <ChangeBadge
                    value={d.monthOverMonth.reviews.change_percent}
                    suffix="%"
                  />
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="mom-card">
                  <p className="mom-label">Negatives</p>
                  <p className="mom-value">{d.metrics.negativeCount}</p>
                  <ChangeBadge
                    value={
                      d.metrics.negativeCount -
                      (Number(d.monthOverMonth.negative.previous) || 0)
                    }
                  />
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="mom-card">
                  <p className="mom-label">Response Rate</p>
                  <p className="mom-value">
                    {d.monthOverMonth.responseRate.current}%
                  </p>
                  <ChangeBadge
                    value={
                      (d.monthOverMonth.responseRate.current ?? 0) -
                      (d.monthOverMonth.responseRate.previous ?? 0)
                    }
                    suffix="%"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <PlatformSection
          d={d}
          number={nPlatform}
          compareLabel={isWeekly ? "vs Last Week" : "Last Month"}
        />

        <div className="mb-4">
          <h6 className="section-title">
            <span className="section-number">{nTrend}</span>
            {isWeekly
              ? "Review Volume – Weekly Trend"
              : "Weekly Trend (This Month)"}
          </h6>

          {isWeekly ? (
            <>
              <div className="d-flex gap-3 mb-3 trend-badges flex-wrap">
                <span className="trend-badge trend-badge-success">
                  Peak: <strong>{d.dayOfWeek.peakDay}</strong>
                </span>
                <span className="trend-badge trend-badge-warning">
                  Lowest: <strong>{d.dayOfWeek.lowDay}</strong>
                </span>
                <span className="trend-badge trend-badge-primary">
                  Consistency: <strong>{d.dayOfWeek.consistency}/100</strong>
                </span>
              </div>

              <div className="weekly-trend-card">
                <div className="weekly-trend-container">
                  {d.dayOfWeek.data.map((item) => {
                    const barHeight = safePct(
                      Math.max(
                        ((Number(item.count) || 0) / maxDayCount) * 100,
                        10,
                      ),
                      10,
                    );
                    const isMax = item.count === maxDayCount;
                    return (
                      <div key={item.day} className="weekly-trend-item">
                        <span
                          className={`weekly-count ${isMax ? "weekly-count-max" : ""}`}
                        >
                          {item.count}
                        </span>
                        <div
                          className={`weekly-bar ${isMax ? "weekly-bar-max" : "weekly-bar-normal"}`}
                          style={{ height: `${barHeight}%`, minHeight: "12px" }}
                        />
                        <span
                          className={`weekly-day ${isMax ? "weekly-day-max" : ""}`}
                        >
                          {item.day}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {d.dayOfWeek.peakDay && (
                <InsightCard>
                  {d.dayOfWeek.peakDay} peak suggests guests review more that
                  day. Send post-visit SMS on {d.dayOfWeek.peakDay} afternoons
                  for max engagement.
                </InsightCard>
              )}
            </>
          ) : (
            <div className="monthly-weekly-trend-card">
              <div className="monthly-weekly-trend-container">
                {d.weeklyTrend.map((week) => {
                  const barHeight = safePct(
                    Math.max(
                      ((Number(week.reviews) || 0) / maxWeekReviews) * 100,
                      10,
                    ),
                    10,
                  );
                  const isMax = week.reviews === maxWeekReviews;
                  return (
                    <div key={week.week} className="monthly-weekly-item">
                      <span
                        className={`monthly-weekly-count ${isMax ? "text-primary fw-bold" : ""}`}
                      >
                        {week.reviews}
                      </span>
                      <span className="monthly-weekly-rating small text-muted">
                        ★{week.rating}
                      </span>
                      <div
                        className={`monthly-weekly-bar ${
                          isMax
                            ? "monthly-weekly-bar-max"
                            : "monthly-weekly-bar-normal"
                        }`}
                        style={{ height: `${barHeight}%`, minHeight: "12px" }}
                      />
                      <span
                        className={`monthly-weekly-label ${
                          isMax ? "text-primary fw-medium" : "text-muted"
                        }`}
                      >
                        {week.week}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <AspectSection d={d} number={nAspect} />
        <EntitySection d={d} number={nEntity} />
        <GuestBehaviourSection d={d} number={nGuest} />
        <EmergingTrendsSection d={d} number={nEmerging} />
        <HighlightsSection d={d} posNumber={nPos} negNumber={nNeg} />
        <QuickWinsSection d={d} number={nQuick} />
        <RecommendationsSection d={d} number={nRec} />
        <OutletsSection d={d} number={nOutlets} />
        <SummarySection
          d={d}
          number={nSummary}
          title={
            isWeekly ? "AI Summary of the Week" : "AI Summary of the Month"
          }
        />
        <ReportFooter />
      </section>
    </div>
  );
};

// ============================================================================
// ANNUAL REPORT
// ============================================================================

const MonthlyTrendLine = ({ data }) => {
  if (data.length < 2) return null;
  const maxReviews = Math.max(...data.map((d) => d.reviews), 1);
  const minRating = Math.min(...data.map((d) => d.rating));
  const maxRating = Math.max(...data.map((d) => d.rating));
  const ratingRange = maxRating - minRating || 1;

  return (
    <div className="monthly-trend-line-wrapper">
      <div className="monthly-trend-bars">
        {data.map((m, i) => (
          <div key={i} className="monthly-trend-bar-item">
            <div
              className="monthly-trend-bar"
              style={{
                height: `${safePct(((Number(m.reviews) || 0) / maxReviews) * 100)}%`,
                minHeight: "4px",
              }}
            />
          </div>
        ))}
      </div>
      <svg className="monthly-trend-overlay" preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke="hsl(45, 93%, 47%)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={data
            .map((m, i) => {
              const x = (i / (data.length - 1)) * 100;
              const y = 100 - ((m.rating - minRating) / ratingRange) * 80 - 10;
              return `${x}%,${y}%`;
            })
            .join(" ")}
        />
        {data.map((m, i) => {
          const x = (i / (data.length - 1)) * 100;
          const y = 100 - ((m.rating - minRating) / ratingRange) * 80 - 10;
          return (
            <circle
              key={i}
              cx={`${x}%`}
              cy={`${y}%`}
              r="4"
              fill="hsl(45, 93%, 47%)"
            />
          );
        })}
      </svg>
      <div className="monthly-trend-labels">
        {data.map((m, i) => (
          <div key={i} className="monthly-trend-label">
            <span>{m.month}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const buildQuarters = (monthlyTrend) => {
  const quarters = { Q1: [], Q2: [], Q3: [], Q4: [] };

  monthlyTrend.forEach((m) => {
    const monthNum = parseInt((m.month_key || "").split("-")[1] || "0", 10);
    if (!monthNum) return;
    const q = "Q" + Math.ceil(monthNum / 3);
    quarters[q].push(m);
  });

  return Object.entries(quarters)
    .filter(([, arr]) => arr.length)
    .map(([q, arr]) => ({
      q,
      reviews: arr.reduce((s, m) => s + m.reviews, 0),
      rating:
        Math.round(
          (arr.reduce((s, m) => s + m.rating * m.reviews, 0) /
            Math.max(
              arr.reduce((s, m) => s + m.reviews, 0),
              1,
            )) *
            100,
        ) / 100,
    }));
};

const AnnuallyReport = ({ data: d }) => {
  const year = (d.period.end || "").slice(0, 4);
  const quarters = buildQuarters(d.monthlyTrend);
  const maxQReviews = Math.max(...quarters.map((q) => q.reviews), 1);

  return (
    <div className="report-container">
      {/* COVER PAGE */}
      <section className="report-section annual-cover-section">
        <header className="d-flex justify-content-between align-items-start mb-4">
          <img src={logo} alt="WowReviews" className="logo" />
          <div className="text-end">
            <p className="annual-cover-tagline">AI-Powered Analytics</p>
          </div>
        </header>

        <div className="annual-cover-content">
          <div className="annual-cover-icon-wrapper">
            <div className="annual-cover-icon">
              <Crown size={40} />
            </div>
            <h6 className="annual-cover-title">Annual Review Report</h6>
            <p className="annual-cover-year">{year}</p>
            <p className="annual-cover-period">
              {d.period.start} – {d.period.end}
            </p>
          </div>

          <div className="annual-cover-outlet-card">
            <div className="annual-cover-outlet-icon">
              <Building size={24} />
            </div>
            <p className="annual-cover-outlet-label">Prepared For</p>
            <p className="annual-cover-outlet-name">{d.outlet}</p>
          </div>

          <div className="row g-3 annual-cover-metrics">
            <div className="col-4">
              <div className="annual-cover-metric annual-cover-metric-success">
                <p className="annual-cover-metric-value">
                  {d.metrics.avgRating}
                </p>
                <p className="annual-cover-metric-label">Avg Rating</p>
                <ChangeBadge value={d.executive.ratingChange} />
              </div>
            </div>
            <div className="col-4">
              <div className="annual-cover-metric annual-cover-metric-primary">
                <p className="annual-cover-metric-value">
                  {d.metrics.totalReviews.toLocaleString()}
                </p>
                <p className="annual-cover-metric-label">Total Reviews</p>
                <ChangeBadge
                  value={d.executive.reviewVolumeChange}
                  suffix="%"
                />
              </div>
            </div>
            <div className="col-4">
              <div className="annual-cover-metric annual-cover-metric-warning">
                <p className="annual-cover-metric-value">
                  {d.executive.healthScore}
                </p>
                <p className="annual-cover-metric-label">Health Score</p>
                <ChangeBadge value={d.executive.scoreChange} suffix=" pts" />
              </div>
            </div>
          </div>

          <p className="annual-cover-generated">Generated: {d.generatedAt}</p>
        </div>

        <footer className="annual-cover-footer">
          <p className="annual-cover-confidential">
            Confidential • For Internal Use Only
          </p>
        </footer>
      </section>

      <div className="page-break" />

      {/* PAGE 2 */}
      <section className="report-section">
        <header className="annual-page-header">
          <div className="d-flex align-items-center gap-2">
            <img src={logo} alt="WowReviews" className="annual-page-logo" />
            <span className="annual-page-title">Annual Report {year}</span>
          </div>
          <span className="annual-page-number">Page 1</span>
        </header>

        <ExecutiveSummarySection
          d={d}
          number={1}
          scoreLabel="Annual Reputation Health Score"
          compareLabel="the previous year"
        />

        <NarrativeSection d={d} number={2} />

        <div className="mb-4">
          <h6 className="section-title annual-section-title">
            <span className="section-number">3</span>
            Key Annual Metrics
          </h6>
          <div className="row g-3">
            <div className="col-md-3 col-6">
              <div className="annual-key-metric annual-metric-primary">
                <BarChart3 size={24} className="annual-key-icon text-primary" />
                <p className="annual-key-value text-primary">
                  {d.metrics.totalReviews.toLocaleString()}
                </p>
                <p className="annual-key-label">Total Reviews</p>
                <ChangeBadge
                  value={d.executive.reviewVolumeChange}
                  suffix="% YoY"
                />
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="annual-key-metric annual-metric-warning">
                <Star size={24} className="annual-key-icon text-warning" />
                <p className="annual-key-value text-warning">
                  {d.metrics.avgRating}
                </p>
                <p className="annual-key-label">Avg Rating</p>
                <ChangeBadge value={d.executive.ratingChange} />
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="annual-key-metric annual-metric-success">
                <Zap size={24} className="annual-key-icon text-success" />
                <p className="annual-key-value text-success">
                  {d.metrics.responseRate}%
                </p>
                <p className="annual-key-label">Reply Rate</p>
              </div>
            </div>
            <div className="col-md-3 col-6">
              <div className="annual-key-metric annual-metric-accent">
                <Users size={24} className="annual-key-icon" />
                <p className="annual-key-value">{d.metrics.qrFeedback}</p>
                <p className="annual-key-label">QR Feedback</p>
              </div>
            </div>
          </div>
        </div>

        {quarters.length > 0 && (
          <div className="mb-4">
            <h6 className="section-title annual-section-title">
              <span className="section-number">4</span>
              Quarterly Performance
            </h6>
            <div className="row g-4">
              <div className="col-md-5 col-12">
                <div className="quarterly-chart-container">
                  {quarters.map((q, i) => (
                    <div key={i} className="quarterly-chart-item">
                      <span className="quarterly-rating">{q.rating}</span>
                      <div
                        className="quarterly-bar"
                        style={{
                          height: `${(q.reviews / maxQReviews) * 100}%`,
                        }}
                      />
                      <span className="quarterly-label">{q.q}</span>
                      <span className="quarterly-count">{q.reviews}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-md-7 col-12">
                <div className="table-card">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Quarter</th>
                        <th className="text-center">Rating</th>
                        <th className="text-center">Reviews</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quarters.map((q, i) => (
                        <tr key={i}>
                          <td className="fw-medium">
                            {q.q} {year}
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center">
                              <StarRating rating={q.rating} />
                            </div>
                          </td>
                          <td className="text-center fw-semibold">
                            {q.reviews}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {d.monthlyTrend.length > 1 && (
          <div className="mb-4">
            <h6 className="section-title annual-section-title">
              <span className="section-number">5</span>
              12-Month Trend Analysis
            </h6>
            <div className="monthly-trend-wrapper">
              <MonthlyTrendLine data={d.monthlyTrend} />
              <div className="monthly-trend-legend">
                <div className="d-flex align-items-center gap-2">
                  <div className="monthly-trend-legend-box" />
                  <span className="small">Review Volume</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="monthly-trend-legend-circle" />
                  <span className="small">Rating Trend</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      <div className="page-break" />

      {/* PAGE 3 */}
      <section className="report-section">
        <header className="annual-page-header">
          <div className="d-flex align-items-center gap-2">
            <img src={logo} alt="WowReviews" className="annual-page-logo" />
            <span className="annual-page-title">Annual Report {year}</span>
          </div>
          <span className="annual-page-number">Page 2</span>
        </header>

        <PlatformSection d={d} number={6} compareLabel="Prev Year" />

        <div className="mb-4">
          <h6 className="section-title annual-section-title">
            <span className="section-number">7</span>
            Annual Sentiment Analysis
          </h6>
          <div className="annual-sentiment-donut">
            <SentimentDonut
              positive={d.metrics.positive}
              neutral={d.metrics.neutral}
              negative={d.metrics.negative}
              size="lg"
            />
          </div>
        </div>

        <AspectSection d={d} number={8} />
        <EntitySection d={d} number={9} />
        <GuestBehaviourSection d={d} number={10} />
      </section>

      <div className="page-break" />

      {/* PAGE 4 */}
      <section className="report-section">
        <header className="annual-page-header">
          <div className="d-flex align-items-center gap-2">
            <img src={logo} alt="WowReviews" className="annual-page-logo" />
            <span className="annual-page-title">Annual Report {year}</span>
          </div>
          <span className="annual-page-number">Page 3</span>
        </header>

        <HighlightsSection d={d} posNumber={11} negNumber={12} />
        <QuickWinsSection d={d} number={13} />
        <RecommendationsSection d={d} number={14} />
        <OutletsSection d={d} number={15} />

        <div className="annual-final-summary">
          <div className="d-flex align-items-start gap-3">
            <div className="annual-final-icon">
              <Crown size={32} />
            </div>
            <div className="flex-fill">
              <h3 className="annual-final-title">
                Year in Review: Executive Summary
              </h3>
              <div className="row g-3 annual-final-grid">
                <div className="col-md-4 col-12">
                  <p className="annual-final-label">Status</p>
                  <p className="annual-final-value text-success">
                    {d.summary.status || "-"}
                  </p>
                </div>
                <div className="col-md-4 col-12">
                  <p className="annual-final-label">Key Metric</p>
                  <p className="annual-final-value text-primary">
                    {d.metrics.positive}% positive across{" "}
                    {d.metrics.totalReviews} reviews
                  </p>
                </div>
                <div className="col-md-4 col-12">
                  <p className="annual-final-label">Top Action</p>
                  <p className="annual-final-value text-warning">
                    {d.summary.action || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ReportFooter />
      </section>
    </div>
  );
};

// ============================================================================
// STATES
// ============================================================================

const ReportLoading = () => (
  <div className="report-state-card">
    <Sparkles size={32} className="text-primary mb-3" />
    <p className="fw-semibold mb-1">Generating AI Report...</p>
    <p className="text-muted small mb-0">
      Deep analysis running — this may take 40–90 seconds.
    </p>
  </div>
);

const ReportEmpty = () => (
  <div className="report-state-card">
    <Shield size={32} className="text-muted mb-3" />
    <p className="fw-semibold mb-1">No reviews found for this period</p>
    <p className="text-muted small mb-0">
      Try a different date range or report type.
    </p>
  </div>
);

const ReportReady = () => (
  <div className="report-state-card">
    <Sparkles size={32} className="text-primary mb-3" />
    <p className="fw-semibold mb-1">Ready to generate your report</p>
    <p className="text-muted small mb-0">
      Select a report type above and click "Generate Report", or open a past
      report from History.
    </p>
  </div>
);

// ============================================================================
// HISTORY DRAWER
// ============================================================================

const TYPE_ICON = { weekly: Calendar, monthly: CalendarDays, annual: Crown };

const HistoryDrawer = ({
  open,
  onClose,
  items,
  loading,
  onSelect,
  activeCacheId,
  filter,
  onFilterChange,
}) => {
  if (!open) return null;

  return (
    <>
      <div className="history-backdrop" onClick={onClose} />
      <aside className="history-drawer">
        <div className="history-head">
          <span className="history-title">
            <History size={16} /> Past Reports
          </span>
          <button className="history-close" onClick={onClose} type="button">
            <X size={18} />
          </button>
        </div>

        <div className="history-filters">
          {["all", "weekly", "monthly", "annual"].map((f) => (
            <button
              key={f}
              type="button"
              className={`history-filter-btn ${filter === f ? "active" : ""}`}
              onClick={() => onFilterChange(f)}
            >
              {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <div className="history-body">
          {loading && (
            <div className="history-loading">
              <Loader2 size={18} className="report-btn-spinner" />
              <span>Loading history...</span>
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="history-empty">
              <History size={26} className="text-muted mb-2" />
              <p className="small text-muted mb-0">
                No saved reports yet. Generate one and it will appear here.
              </p>
            </div>
          )}

          {!loading &&
            items.map((it) => {
              const Icon = TYPE_ICON[it.report_type] || FileText;
              const isActive = String(activeCacheId) === String(it.cache_id);
              return (
                <button
                  key={it.cache_id}
                  type="button"
                  className={`history-item ${isActive ? "active" : ""}`}
                  onClick={() => onSelect(it)}
                >
                  <span className={`history-item-icon type-${it.report_type}`}>
                    <Icon size={14} />
                  </span>
                  <span className="history-item-body">
                    <span className="history-item-top">
                      <span className="history-item-type">
                        {it.report_type}
                      </span>
                      <span className="history-item-range">
                        {it.start_date} → {it.end_date}
                      </span>
                    </span>
                    {it.headline && (
                      <span className="history-item-headline">
                        {it.headline}
                      </span>
                    )}
                    <span className="history-item-time">
                      <Clock size={11} /> {it.generated_at}
                    </span>
                  </span>
                </button>
              );
            })}
        </div>
      </aside>
    </>
  );
};

// ============================================================================
// MAIN PAGE
// ============================================================================

const EMPTY_SLOT = {
  data: null,
  hasSearched: false,
  cacheId: null,
  generatedAt: "",
  archived: false,
};

const ReportsPage = () => {
  const [reportType, setReportType] = useState("weekly");

  // ---- Har tab ka apna slot — tab switch par data KHOTA NAHI ----
  const [slots, setSlots] = useState({
    weekly: { ...EMPTY_SLOT },
    monthly: { ...EMPTY_SLOT },
    annual: { ...EMPTY_SLOT },
  });

  const [loadingType, setLoadingType] = useState(null); // kis tab par spinner
  const [downloading, setDownloading] = useState(false);

  // ---- History ----
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState("all");

  const slot = slots[reportType];
  const loading = loadingType === reportType;

  const patchSlot = useCallback((type, patch) => {
    setSlots((prev) => ({ ...prev, [type]: { ...prev[type], ...patch } }));
  }, []);

  // -------------------------------------------------------------------
  // Generate (fresh / cached)
  // -------------------------------------------------------------------

  const fetchReportData = async (forceRefresh = false) => {
    const type = reportType;
    setLoadingType(type);
    patchSlot(type, { hasSearched: true });

    try {
      const end = new Date();
      const start = new Date();
      const days = type === "weekly" ? 7 : type === "monthly" ? 30 : 365;
      start.setDate(end.getDate() - (days - 1));

      const fmt = (dt) => {
        const off = dt.getTimezoneOffset();
        const local = new Date(dt.getTime() - off * 60000);
        return local.toISOString().slice(0, 10);
      };

      const formdata = new FormData();
      formdata.append("start_date", fmt(start));
      formdata.append("end_date", fmt(end));
      formdata.append("report_type", type);
      if (forceRefresh) formdata.append("force_refresh", "1");

      const response = await server_post_data(get_ai_business_report, formdata);
      const res =
        typeof response === "string" ? JSON.parse(response) : response;

      if (res.error) {
        console.error("Report error:", res.message);
        patchSlot(type, { data: null, cacheId: null, archived: false });
      } else {
        patchSlot(type, {
          data: mapApiToReport(res.data),
          cacheId: res.cache_id || null,
          generatedAt: res.generated_at || "",
          archived: false,
        });
        // naya report ban gaya to history stale ho gayi
        if (historyOpen) loadHistory(historyFilter);
      }
    } catch (error) {
      console.error("Fetch or parsing error:", error);
      patchSlot(type, { data: null });
    } finally {
      setLoadingType(null);
    }
  };

  // -------------------------------------------------------------------
  // History
  // -------------------------------------------------------------------

  const loadHistory = useCallback(async (filter) => {
    setHistoryLoading(true);
    try {
      const fd = new FormData();
      fd.append("report_type", filter || "all");
      fd.append("limit", "40");

      const response = await server_post_data(get_ai_report_history, fd);
      const res =
        typeof response === "string" ? JSON.parse(response) : response;

      setHistoryItems(res.error ? [] : res.data || []);
    } catch (e) {
      console.error("History load failed:", e);
      setHistoryItems([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const openHistory = () => {
    setHistoryOpen(true);
    loadHistory(historyFilter);
  };

  const changeHistoryFilter = (f) => {
    setHistoryFilter(f);
    loadHistory(f);
  };

  const openArchivedReport = async (item) => {
    // Archived report uske apne type wale tab me load hoti hai,
    // aur us tab par switch ho jate hain
    const type = ["weekly", "monthly", "annual"].includes(item.report_type)
      ? item.report_type
      : "weekly";

    setReportType(type);
    setHistoryOpen(false);
    setLoadingType(type);
    patchSlot(type, { hasSearched: true });

    try {
      const fd = new FormData();
      fd.append("cache_id", item.cache_id);

      const response = await server_post_data(get_ai_report_detail, fd);
      const res =
        typeof response === "string" ? JSON.parse(response) : response;

      if (res.error) {
        patchSlot(type, { data: null, cacheId: null, archived: false });
      } else {
        patchSlot(type, {
          data: mapApiToReport(res.data),
          cacheId: res.cache_id,
          generatedAt: res.generated_at || "",
          archived: true,
        });
      }
    } catch (e) {
      console.error("Archived report load failed:", e);
      patchSlot(type, { data: null });
    } finally {
      setLoadingType(null);
    }
  };

  // -------------------------------------------------------------------
  // Tab switch — sirf reportType badalta hai, data intact rehta hai
  // -------------------------------------------------------------------

  const handleTypeChange = (type) => {
    if (downloading) return;
    setReportType(type);
  };

  // -------------------------------------------------------------------
  // Download
  // -------------------------------------------------------------------

  const getFilename = () => {
    if (!slot.data) return "report.pdf";

    if (reportType === "annual") {
      const year = (slot.data.period.end || "").slice(0, 4);
      return `Annual_Report_${slot.data.outlet}_${year}.pdf`;
    }

    const label = reportType === "weekly" ? "Weekly_Report" : "Monthly_Report";
    return `${label}_${slot.data.outlet}_${slot.data.period.end}.pdf`;
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadReportPDF(getFilename());
    } catch (e) {
      console.error("PDF download failed:", e);
    } finally {
      setDownloading(false);
    }
  };

  // ESC se drawer band
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setHistoryOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <MasterLayout>
      <Breadcrumb title="Review Summary Report" onDateSelection={null} />

      <div className="reports-page-wrapper">
        <div className="report-toolbar d-print-none">
          <div className="report-switcher-container">
            {[
              { key: "weekly", label: "Weekly", Icon: Calendar },
              { key: "monthly", label: "Monthly", Icon: CalendarDays },
              { key: "annual", label: "Annual", Icon: Crown },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                className={`report-switcher-btn ${reportType === key ? "active" : ""}`}
                onClick={() => handleTypeChange(key)}
              >
                <Icon size={16} />
                <span>{label}</span>
                {/* jis tab me report already loaded hai uspar dot */}
                {slots[key].data && <span className="tab-loaded-dot" />}
                {loadingType === key && (
                  <Loader2 size={12} className="report-btn-spinner" />
                )}
              </button>
            ))}
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              onClick={openHistory}
              className="btn btn-outline-secondary report-print-btn"
              type="button"
            >
              <History size={16} className="me-2" />
              History
            </button>

            <button
              onClick={() => fetchReportData(false)}
              disabled={loading || downloading}
              className="btn btn-primary report-print-btn"
            >
              {loading ? (
                <Loader2 size={16} className="me-2 report-btn-spinner" />
              ) : (
                <Sparkles size={16} className="me-2" />
              )}
              {loading ? "Generating..." : "Generate Report"}
            </button>

            {slot.data && !loading && (
              <button
                onClick={() => fetchReportData(true)}
                disabled={downloading}
                className="btn btn-outline-secondary report-print-btn"
                title="Ignore cache and re-run AI"
                type="button"
              >
                <RefreshCw size={16} className="me-2" />
                Re-run
              </button>
            )}

            {slot.data && !loading && (
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="btn btn-outline-primary report-print-btn"
              >
                {downloading ? (
                  <Loader2 size={16} className="me-2 report-btn-spinner" />
                ) : (
                  <Download size={16} className="me-2" />
                )}
                {downloading ? "Preparing PDF..." : "Download PDF"}
              </button>
            )}
          </div>
        </div>

        {/* Archived banner */}
        {slot.archived && slot.data && !loading && (
          <div className="archived-banner d-print-none">
            <span>
              <History size={14} /> Viewing a saved report generated on{" "}
              <strong>{slot.generatedAt}</strong>
            </span>
            <button type="button" onClick={() => fetchReportData(false)}>
              Back to latest
            </button>
          </div>
        )}

        <div className="report-content-area animate-fade-in">
          {loading && <ReportLoading />}
          {!loading && !slot.hasSearched && <ReportReady />}
          {!loading && slot.hasSearched && !slot.data && <ReportEmpty />}
          {!loading && slot.data && reportType === "weekly" && (
            <PeriodReport data={slot.data} type="weekly" />
          )}
          {!loading && slot.data && reportType === "monthly" && (
            <PeriodReport data={slot.data} type="monthly" />
          )}
          {!loading && slot.data && reportType === "annual" && (
            <AnnuallyReport data={slot.data} />
          )}
        </div>
      </div>

      <HistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        items={historyItems}
        loading={historyLoading}
        onSelect={openArchivedReport}
        activeCacheId={slot.cacheId}
        filter={historyFilter}
        onFilterChange={changeHistoryFilter}
      />
    </MasterLayout>
  );
};

export default ReportsPage;
